/**
 * Cookie helpers used for cross-subdomain identity. We deliberately use cookies
 * (not localStorage) for the visitor id, session and user id, because cookies
 * can be scoped to the registrable root domain (e.g. `.jetvisa.io`) and are then
 * shared across every subdomain — localStorage is always origin-isolated.
 */

export function getCookie(name: string): string | null {
  const prefix = name + "=";
  const parts = document.cookie ? document.cookie.split("; ") : [];
  for (const part of parts) {
    if (part.indexOf(prefix) === 0) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

export interface CookieOptions {
  /** Registrable domain to scope to (without leading dot), e.g. "jetvisa.io". */
  domain?: string;
  /** Days until expiry (default ~2 years; 0 = session cookie). */
  days?: number;
}

export function setCookie(
  name: string,
  value: string,
  opts: CookieOptions = {},
): void {
  const { domain, days = 730 } = opts;
  let str = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax`;
  if (days > 0) {
    const d = new Date(Date.now() + days * 86_400_000);
    str += `; expires=${d.toUTCString()}`;
  }
  if (domain) str += `; domain=.${domain}`;
  if (location.protocol === "https:") str += "; Secure";
  try {
    document.cookie = str;
  } catch {
    /* cookies blocked — caller falls back to localStorage */
  }
}

export function deleteCookie(name: string, domain?: string): void {
  let str = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  if (domain) str += `; domain=.${domain}`;
  try {
    document.cookie = str;
  } catch {
    /* ignore */
  }
}

/**
 * Resolve the registrable ("eTLD+1") domain for the current host, so a cookie
 * set there is visible to every subdomain. We can't ship a public-suffix list,
 * so we lean on the browser: walk candidate parent domains from broadest to
 * narrowest and pick the first one the browser actually lets us set a cookie on.
 * Browsers refuse cookies on public suffixes (e.g. `.co.uk`), which makes this
 * yield the true registrable domain (e.g. `foo.co.uk`, `jetvisa.io`).
 *
 * Returns null for hosts where subdomain cookies don't apply (localhost, raw
 * IPs, single-label hosts) — the caller then uses host-scoped storage.
 */
export function getRegistrableDomain(): string | null {
  const host = location.hostname;
  if (
    !host ||
    host === "localhost" ||
    host.endsWith(".localhost") ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(host) ||
    host.indexOf(":") !== -1 || // IPv6
    host.indexOf(".") === -1
  ) {
    return null;
  }

  const parts = host.split(".");
  const test = "_wo_probe";
  // i = parts.length - 2 gives the broadest 2-label candidate; walk narrower.
  for (let i = parts.length - 2; i >= 0; i--) {
    const candidate = parts.slice(i).join(".");
    document.cookie = `${test}=1; domain=.${candidate}; path=/; SameSite=Lax`;
    if (getCookie(test) === "1") {
      deleteCookie(test, candidate);
      return candidate;
    }
  }
  return null;
}
